import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft, ExternalLink, Copy } from 'lucide-react';

/**
 * MeetingRoom
 * -----------
 * Embeds Jitsi Meet directly inside the PWA using the External API so the
 * currently signed-in user's name / email / avatar are pre-filled and there's
 * no context switch out of the app.
 *
 * Route:  /meeting/:meetingId
 * Fallback: an "Open in browser" button for guest attendees or if the
 * external script fails to load.
 */

const JITSI_HOST_DEFAULT = 'meet.jit.si';

const loadJitsiScript = (host) => new Promise((resolve, reject) => {
  if (window.JitsiMeetExternalAPI) return resolve(window.JitsiMeetExternalAPI);
  const existing = document.getElementById('jitsi-external-api');
  if (existing) {
    existing.addEventListener('load', () => resolve(window.JitsiMeetExternalAPI));
    existing.addEventListener('error', reject);
    return;
  }
  const s = document.createElement('script');
  s.id = 'jitsi-external-api';
  s.src = `https://${host}/external_api.js`;
  s.async = true;
  s.onload = () => resolve(window.JitsiMeetExternalAPI);
  s.onerror = () => reject(new Error('Failed to load Jitsi'));
  document.head.appendChild(s);
});

// Extract "host" + "room" from a meet URL like https://meet.jit.si/Zazu-Team-abc
const parseMeetUrl = (url) => {
  try {
    const u = new URL(url);
    const room = u.pathname.replace(/^\/+/, '').split('/')[0];
    return { host: u.host, room };
  } catch {
    return { host: JITSI_HOST_DEFAULT, room: 'Zazu-Meeting' };
  }
};

const MeetingRoom = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/meetings/${meetingId}`);
        if (!cancelled) setMeeting(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Meeting not found or you do not have access.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [meetingId, api]);

  const { host, room } = useMemo(() => (meeting ? parseMeetUrl(meeting.meet_url) : { host: JITSI_HOST_DEFAULT, room: '' }), [meeting]);

  useEffect(() => {
    if (!meeting || !containerRef.current) return;
    let disposed = false;
    (async () => {
      try {
        const JitsiMeetExternalAPI = await loadJitsiScript(host);
        if (disposed || !containerRef.current) return;

        const configOverwrite = {
          startWithAudioMuted: !!meeting.mute_on_join,
          startWithVideoMuted: !!(meeting.video_off_on_join || meeting.call_type === 'audio'),
          prejoinPageEnabled: false,
          disableDeepLinking: true,      // don't try to open the Jitsi native app
          enableLobbyChat: !!meeting.waiting_room,
          lobby: { enabled: !!meeting.waiting_room },
        };
        if (meeting.password) configOverwrite.roomPassword = meeting.password;

        apiRef.current = new JitsiMeetExternalAPI(host, {
          roomName: room,
          parentNode: containerRef.current,
          width: '100%',
          height: '100%',
          userInfo: {
            displayName: user?.name || 'Guest',
            email: user?.email || undefined,
          },
          configOverwrite,
          interfaceConfigOverwrite: {
            MOBILE_APP_PROMO: false,
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_POWERED_BY: false,
          },
        });

        // Auto-set the password on join (host only — Jitsi will silently ignore for participants)
        if (meeting.password) {
          apiRef.current.addListener('participantRoleChanged', (ev) => {
            if (ev.role === 'moderator') {
              try { apiRef.current.executeCommand('password', meeting.password); } catch {}
            }
          });
        }
        apiRef.current.addListener('readyToClose', () => navigate(-1));
      } catch (e) {
        if (!disposed) setError('Could not load the meeting embed. Use "Open in browser" instead.');
      }
    })();

    return () => {
      disposed = true;
      try { apiRef.current?.dispose(); } catch {}
      apiRef.current = null;
    };
  }, [meeting, host, room, user, navigate]);

  const openInBrowser = () => {
    if (meeting?.meet_url) window.open(meeting.meet_url, '_blank', 'noopener');
  };

  const copyLink = () => {
    if (meeting?.meet_url) {
      navigator.clipboard.writeText(meeting.meet_url);
      toast.success('Meeting link copied');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 p-6">
        <div className="max-w-md text-center bg-white rounded-2xl p-6 shadow-xl">
          <p className="text-slate-800 font-semibold mb-4" data-testid="meeting-room-error">{error}</p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            {meeting?.meet_url && (
              <Button onClick={openInBrowser} className="bg-teal-600 hover:bg-teal-700">
                <ExternalLink className="w-4 h-4 mr-2" /> Open in browser
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-slate-900" data-testid="meeting-room-page">
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-950/80 backdrop-blur border-b border-slate-800 text-slate-200">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-slate-200 hover:bg-slate-800" data-testid="meeting-room-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <p className="font-fredoka font-semibold truncate max-w-[60vw]" data-testid="meeting-room-title">
            {meeting?.title || 'Meeting'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={copyLink} className="text-slate-200 hover:bg-slate-800" data-testid="meeting-room-copy">
            <Copy className="w-4 h-4 mr-1" /> Copy link
          </Button>
          <Button variant="ghost" size="sm" onClick={openInBrowser} className="text-slate-200 hover:bg-slate-800" data-testid="meeting-room-open-browser">
            <ExternalLink className="w-4 h-4 mr-1" /> Browser
          </Button>
        </div>
      </div>

      {/* Jitsi iframe container fills the rest */}
      <div ref={containerRef} className="flex-1 min-h-0" data-testid="meeting-room-jitsi-container" />
    </div>
  );
};

export default MeetingRoom;
